import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ArrowIcon from '../../assets/icons/ArrowIcon';
import { selectDealersList } from '../../reducers/dealersListReducer';
import { MARKET_UNITS_SHORT } from '../../constants/radiusValues';
import {
  DealersSelectorButton,
  Label,
  Hint,
  SelectedResult,
  Count,
  CountWrapper,
} from './DealersSelector.styles';

interface DealersSelectorProps {
  onOpen: () => void;
}

const DealersSelector: React.FC<DealersSelectorProps> = ({ onOpen }) => {
  const { t } = useTranslation();
  const { radius, centerLocation, dealersWithinRadius, selectedDealersIds } =
    useSelector(selectDealersList);

  const handleOpenList = () => {
    onOpen();
  };

  const isDisabled = useMemo(() => {
    if (!centerLocation || !dealersWithinRadius?.length) return true;
    return false;
  }, [centerLocation, dealersWithinRadius?.length]);

  const selectedResult = useMemo(() => {
    if (!centerLocation) {
      return <Count disabled>{t('dealerLocationSearch.dealersList.reflect')}</Count>;
    }

    if (selectedDealersIds.length) {
      return (
        <CountWrapper>
          {t('dealerLocationSearch.dealersList.selected')}:
          <Count>{selectedDealersIds.length}</Count>
        </CountWrapper>
      );
    }

    return (
      <CountWrapper disabled={isDisabled}>
        <Count disabled={isDisabled}>
          {dealersWithinRadius?.length} {t('dealerLocationSearch.dealersList.dealers')}
        </Count>{' '}
        {t('dealerLocationSearch.dealersList.within')} {radius}
        {MARKET_UNITS_SHORT}
      </CountWrapper>
    );
  }, [
    centerLocation,
    selectedDealersIds.length,
    isDisabled,
    dealersWithinRadius?.length,
    radius,
    t,
  ]);

  return (
    <>
      <DealersSelectorButton disabled={isDisabled} onClick={handleOpenList}>
        <Hint disabled={isDisabled}>{t('dealerLocationSearch.dealersList.optional')}</Hint>
        <Label disabled={isDisabled}>
          {t('dealerLocationSearch.dealersList.selectWithinRadius')}{' '}
          <ArrowIcon disabled={isDisabled} />
        </Label>
        <SelectedResult>{selectedResult}</SelectedResult>
      </DealersSelectorButton>
    </>
  );
};

export default DealersSelector;
